import { useCallback, useContext, useState } from "react"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Help,
    OutlinedButton,
    Text,
    Title,
    ToggleSwitchButton
} from "dash-styled"
import { ReactComponent as AddIcon } from "icons/lib/plus.svg"
import { ReactComponent as TrashIcon } from "icons/lib/trash.svg"
import { ReactComponent as ArrowDown } from "icons/lib/arrow-down.svg"
import { ReactComponent as PenIcon } from "icons/lib/pen-outlined.svg"
import {
    AnnotationFieldContent,
    AnnotationFields
} from "universal-schema/lib/1.0.0/annotation"

import {
    DataFieldType,
    DocumentType,
    FieldStatus
} from "../../../../../../Models/DataProgram/DocumentExtraction"
import { SchemaFormDeleteModal } from "../../../Modals"
import {
    FieldModal,
    FieldModalChildProps,
    FieldModalType
} from "../../Common/Modals/FieldModal"
import {
    DocumentsListItemProps,
    useDocumentsListItem
} from "../../Common/Hooks/useDocumentsListItem"
import { DocumentsExtractionContext } from "../../DocumentsExtractionContext"
import { AnnotationFieldsStateContext } from "../AnnotationFieldsStateContext"
import { BoundingBoxStatus } from "../constants"
import {
    checkFieldValid,
    isKeyValueAnnotation,
    isTableAnnotation
} from "../utils"
import { DocumentEdit } from "./DocumentEdit"
import { BoundingBoxInstructions } from "./BoundingBoxInstructions"

const EditIcon = () => <PenIcon />
const DeleteIcon = () => <TrashIcon />

export const DocumentsListItem = ({
    schema,
    value: { documentId, documentType, annotationFields = [], ...restValue },
    onDelete,
    onEdit
}: DocumentsListItemProps) => {
    const {
        editDocumentState: [
            showEditDocument,
            openEditDocumentModal,
            closeEditDocumentModal
        ],
        deleteModalState: [showDeleteModal, , closeDeleteModal],
        deleteHandlerState: [deleteHandler],
        openDeleteDocument: openDeleteDocumentModal,
        openFieldDelete,
        onStatusChange,
        onFieldChange,
        onFieldDelete,
        onDocumentSubmit
    } = useDocumentsListItem({
        schema,
        value: { documentId, documentType, annotationFields, ...restValue },
        onDelete,
        onEdit
    })

    const {
        showFieldModal,
        fieldModalOptions,
        setStatus,
        openNewField,
        openFieldEdit,
        closeFieldModal,
        setCurrentBoundingBoxes,
        setCurrentAnnotations,
        setVisiblePage,
        rendererHelpers,
        processedBoundingBoxes
    } = useContext(AnnotationFieldsStateContext)

    const { annotationsOutputList, setAnnotationsOutputList, findOutputId } =
        useContext(DocumentsExtractionContext)

    const [annotations, setAnnotations] =
        useState<AnnotationFields[""] | undefined>()

    const handleCloseFieldModal = () => {
        setAnnotations(undefined)
        closeFieldModal()
    }

    const showPrimitiveFieldAnnotations = (
        fieldId: string,
        fieldAnnotations: AnnotationFieldContent[]
    ) => {
        setCurrentAnnotations(fieldAnnotations)
        const shapeIds = processedBoundingBoxes.shapesState[fieldId]
        if (fieldAnnotations?.[0]?.pageNumber) {
            setVisiblePage(fieldAnnotations?.[0]?.pageNumber)
            rendererHelpers?.setShowingPage(fieldAnnotations?.[0]?.pageNumber)
        }
        setCurrentBoundingBoxes({
            boundingBoxes: shapeIds
                ? processedBoundingBoxes.boundingBoxes.filter(({ id }) =>
                      shapeIds.includes(id)
                  )
                : [],
            shapesState: {
                [fieldId]: shapeIds
            }
        })
    }

    const handleOpenEditField = (fieldIndex: number) => () => {
        const fieldId = annotationFields[fieldIndex].fieldId.data
        const annotationsOutputId = findOutputId(fieldId)
        const fieldAnnotations = annotationsOutputId
            ? annotationsOutputList?.annotations[annotationsOutputId]
            : undefined

        if (
            annotationsOutputId !== undefined &&
            fieldAnnotations !== undefined &&
            fieldAnnotations.length > 0
        ) {
            if (isKeyValueAnnotation(fieldAnnotations[0])) {
                // TODO: cover key,value pair
            } else if (
                isTableAnnotation(
                    fieldAnnotations?.[0] as AnnotationFieldContent
                )
            ) {
                // TODO: cover table type
            } else {
                showPrimitiveFieldAnnotations(
                    annotationsOutputId,
                    fieldAnnotations
                )
            }
            setStatus(BoundingBoxStatus.TRUSTED)
        } else {
            setCurrentBoundingBoxes(undefined)
            setStatus(BoundingBoxStatus.ACTIVE)
        }
        openFieldEdit({
            index: fieldIndex,
            type: FieldModalType.Edit
        })
    }

    const handleFieldModalSubmit = (field: DataFieldType) => {
        const newList = annotationsOutputList
        if (newList) {
            const requireAnnotations =
                field?.options?.data?.requireAnnotations?.selection
            const annotationsOutputId = findOutputId(field?.fieldId.data)
            if (annotationsOutputId) {
                setAnnotationsOutputList({
                    ...newList,
                    annotations: {
                        ...newList?.annotations,
                        [annotationsOutputId]:
                            requireAnnotations && annotations ? annotations : []
                    }
                })
            }
        }
        onFieldChange(field, fieldModalOptions)
    }

    const handleDocumentSubmit = (value: DocumentType) => {
        onDocumentSubmit(value)
        if (annotationsOutputList) {
            setAnnotationsOutputList({
                ...annotationsOutputList,
                annotations: {}
            })
        }
    }

    const handleStatusChange =
        (
            type: "annotationFields" | "presetFields",
            prevValue: FieldStatus,
            index: number,
            fieldId: string
        ) =>
        () => {
            const newList = annotationsOutputList
            if (newList) {
                const annotationsOutputId = findOutputId(fieldId)
                if (annotationsOutputId) {
                    setAnnotationsOutputList({
                        ...newList,
                        annotations: {
                            ...newList?.annotations,
                            [annotationsOutputId]: []
                        }
                    })
                }
            }
            onStatusChange(type, prevValue, index)
        }

    const Instructions = useCallback(
        (props: FieldModalChildProps) => {
            return (
                <BoundingBoxInstructions {...props} onSubmit={setAnnotations} />
            )
        },
        [setAnnotations]
    )

    if (showEditDocument) {
        return (
            <DocumentEdit
                schema={schema}
                onSubmit={handleDocumentSubmit}
                onClose={closeEditDocumentModal}
                value={{
                    documentType,
                    documentId
                }}
                templates={[]}
            />
        )
    }

    if (showFieldModal) {
        return (
            <FieldModal
                mode="paper"
                type={fieldModalOptions.type}
                schema={schema}
                docType={documentType}
                onSubmit={handleFieldModalSubmit}
                onClose={handleCloseFieldModal}
                index={
                    fieldModalOptions.type === FieldModalType.Edit &&
                    fieldModalOptions.index !== undefined
                        ? fieldModalOptions.index
                        : annotationFields.length
                }
                value={
                    fieldModalOptions.index !== undefined
                        ? annotationFields[fieldModalOptions.index]
                        : undefined
                }
                children={Instructions}
            />
        )
    }

    return (
        <>
            <div
                className="m-2.5 mb-4 border border-solid rounded-lg"
                style={{
                    maxHeight: "calc(100% - 25px)"
                }}
            >
                <Accordion
                    defaultExpanded
                    elevation={0}
                    classes={{
                        root: "flex flex-col max-h-full"
                    }}
                    TransitionProps={{
                        style: {
                            overflow: "auto"
                        }
                    }}
                >
                    <AccordionSummary
                        className="flex-row-reverse min-h-13 bg-gray-250 rounded text-rhino-970"
                        classes={{
                            content: "flex items-center my-2"
                        }}
                        expandIcon={<ArrowDown />}
                    >
                        <Title
                            component="h3"
                            variant="heading4"
                            className="uppercase w-full m-auto ml-3 leading-4"
                        >
                            {documentType}
                        </Title>
                        <OutlinedButton
                            Icon={EditIcon}
                            label=""
                            marginLeft="2"
                            color="transparent"
                            className="text-rhino-970 py-3"
                            onClick={openEditDocumentModal}
                            onFocus={event => event.stopPropagation()}
                        />
                        <OutlinedButton
                            Icon={DeleteIcon}
                            label=""
                            marginLeft="2"
                            color="transparent"
                            className="text-rhino-970 py-3"
                            onClick={openDeleteDocumentModal}
                            onFocus={event => event.stopPropagation()}
                        />
                    </AccordionSummary>
                    <AccordionDetails className="flex flex-col p-2 bg-marketplace-bg-grey">
                        <OutlinedButton
                            Icon={AddIcon}
                            label="Add new field"
                            className="self-end py-3 z-5 bg-marketplace-bg-grey border-gray-500"
                            onClick={openNewField}
                        />
                        <div className="w-full overflow-y-auto">
                            {annotationFields?.map((fieldData, fieldIndex) => {
                                const {
                                    fieldId,
                                    fieldName,
                                    dataType,
                                    options,
                                    status
                                } = fieldData
                                const requireAnnotation = !checkFieldValid(
                                    findOutputId(fieldId.data),
                                    fieldData,
                                    annotationsOutputList
                                )
                                return (
                                    <div
                                        key={fieldId.data}
                                        className="w-full flex"
                                    >
                                        <div className="w-4/12 flex items-center rounded-l pl-2 bg-transparent border-b-rhino-300 border-b-0">
                                            {requireAnnotation && (
                                                <Help
                                                    className="mr-2 text-coral-700"
                                                    text="Annotate the field. Click on edit button to add the bounding box."
                                                    tooltipPlacement="bottom"
                                                />
                                            )}
                                            <Title
                                                component="h4"
                                                variant="heading4"
                                                className="capitalize m-0"
                                            >
                                                {fieldName.data}
                                            </Title>
                                        </div>
                                        <div className="w-5/12 bg-transparent border-b-rhino-300">
                                            <Text>
                                                {dataType?.data ||
                                                    options?.data?.contentType}
                                            </Text>
                                        </div>
                                        <div className="w-3/12 flex items-center justify-end rounded-r pr-2 bg-transparent border-b-rhino-300">
                                            <ToggleSwitchButton
                                                fullWidth={false}
                                                iconSize="lg"
                                                className="w-auto"
                                                offClass=""
                                                value={
                                                    status.data ===
                                                    FieldStatus.Active
                                                }
                                                activeColor="#00B092"
                                                inactiveColor="#EA5757"
                                                onChange={handleStatusChange(
                                                    "annotationFields",
                                                    status.data,
                                                    fieldIndex,
                                                    fieldId.data
                                                )}
                                            />
                                            <OutlinedButton
                                                Icon={EditIcon}
                                                label=""
                                                marginLeft="2"
                                                padding="p-2"
                                                className="bg-transparent"
                                                onClick={handleOpenEditField(
                                                    fieldIndex
                                                )}
                                            />
                                            <OutlinedButton
                                                label=""
                                                marginLeft="2"
                                                padding="p-2"
                                                className="bg-transparent"
                                                Icon={DeleteIcon}
                                                onClick={openFieldDelete(
                                                    onFieldDelete(fieldIndex)
                                                )}
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </AccordionDetails>
                </Accordion>
            </div>
            {showDeleteModal && (
                <SchemaFormDeleteModal
                    onClose={closeDeleteModal}
                    onSubmit={deleteHandler}
                />
            )}
        </>
    )
}
